/**
 * AssetPicker — modal dialog that lets the operator pick an asset already
 * present in a MinIO bucket.
 *
 * Replaces the legacy `<asset-picker>` Custom Element. The legacy modal was
 * a single-page list with a manual debounce filter; this rewrite preserves
 * that contract end-to-end (search by key, cursor pagination, click → select)
 * but plugs into the design system primitives (`Dialog`, `Input`, `Button`,
 * `Skeleton`, `EmptyState`, `ErrorState`) and TanStack Query for fetching.
 *
 * The component is uncontrolled w.r.t. internal navigation (search term,
 * cursor stack) but fully controlled w.r.t. visibility — `open` / `onCancel`
 * are owned by the parent so the picker can be embedded next to other form
 * fields without leaking modal state upward.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { File as FileIcon, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@favelafood/shared/components/ui/Dialog';
import { Button } from '@favelafood/shared/components/ui/Button';
import { Input } from '@favelafood/shared/components/ui/Input';
import { Skeleton } from '@favelafood/shared/components/ui/Skeleton';
import { EmptyState } from '@favelafood/shared/components/ui/EmptyState';
import { ErrorState } from '@favelafood/shared/components/ui/ErrorState';
import { api } from '@favelafood/shared/api/client';
import { cn } from '@favelafood/shared/lib/cn';

/** Debounce delay applied to the live search input. */
const SEARCH_DEBOUNCE_MS = 300;
/** Page size requested from the backend (matches legacy implementation). */
const PAGE_SIZE = 50;
/** Number of skeleton tiles shown while loading. */
const SKELETON_COUNT = 12;
/** Cap the asset name display so tiles stay aligned. */
const MAX_NAME_LENGTH = 24;

/**
 * Public asset shape exposed to consumers via `onSelect`. Matches the
 * legacy event payload (`publicUrl` + `objectKey` aliased as `key`) plus
 * the metadata needed to render image previews.
 */
export interface PickedAsset {
  /** Object key in the bucket (e.g. `2025/banner-hero.png`). */
  key: string;
  /** Public URL when bucket is public; may be empty for private buckets. */
  publicUrl: string;
  /** MIME type returned by the storage service (may be missing). */
  contentType?: string;
  /** Size in bytes, when available. */
  size?: number;
}

export interface AssetPickerProps {
  /** Bucket to list. Cannot be changed while open without remounting. */
  bucket: string;
  /** Whether the modal is shown. Controlled by the parent. */
  open: boolean;
  /** Fires when the operator picks an asset. The parent typically closes the modal. */
  onSelect: (asset: PickedAsset) => void;
  /** Fires when the operator dismisses the modal (Esc, overlay click, X). */
  onCancel: () => void;
}

/* ------------------------------------------------------------------ */
/* Backend response models                                             */
/* ------------------------------------------------------------------ */

/**
 * Runtime shape of `GET /api/v1/admin/storage/buckets/{name}/objects`. The
 * generated OpenAPI types do not yet include the admin-storage endpoints,
 * so we model the contract here and validate at the boundary.
 */
interface ObjectListResponse {
  items: ObjectSummary[];
  cursor: string | null;
  hasMore: boolean;
}

interface ObjectSummary {
  key: string;
  size: number;
  contentType?: string;
  lastModified?: string;
  publicUrl: string;
}

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

/**
 * Returns a debounced copy of `value` that only updates after `delay` ms
 * of stillness. Implemented with `setTimeout` cleanup to avoid leaks when
 * the component unmounts mid-debounce.
 */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/* ------------------------------------------------------------------ */
/* Fetch helper                                                        */
/* ------------------------------------------------------------------ */

/** Build the URL for a given bucket + cursor. */
function buildListUrl(bucket: string, cursor: string | null): string {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (cursor) params.set('cursor', cursor);
  return `/api/v1/admin/storage/buckets/${encodeURIComponent(bucket)}/objects?${params.toString()}`;
}

/**
 * Fetches a page of objects. Goes through `fetch` directly (with the auth
 * header pulled from local storage) because the OpenAPI schema doesn't
 * expose this endpoint yet. When the schema is regenerated we can swap
 * this for `api.GET('/api/v1/admin/storage/buckets/{name}/objects', ...)`.
 */
async function fetchObjects(
  bucket: string,
  cursor: string | null,
): Promise<ObjectListResponse> {
  const url = buildListUrl(bucket, cursor);
  const result = (await api.GET(url as never, {
    params: { path: { name: bucket } },
  } as never)) as { data?: ObjectListResponse; error?: unknown };
  if (!result.data) {
    throw new Error('Falha ao listar objetos do bucket');
  }
  return normalizeListResponse(result.data);
}

/** Defensive normalization — server may return null for cursor / undefined for items. */
function normalizeListResponse(raw: ObjectListResponse): ObjectListResponse {
  return {
    items: Array.isArray(raw.items) ? raw.items : [],
    cursor: raw.cursor ?? null,
    hasMore: Boolean(raw.hasMore),
  };
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function isImageContentType(contentType?: string): boolean {
  return Boolean(contentType && contentType.startsWith('image/'));
}

function truncateName(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

function filterByQuery(items: ObjectSummary[], query: string): ObjectSummary[] {
  if (!query) return items;
  const needle = query.toLowerCase();
  return items.filter((o) => o.key.toLowerCase().includes(needle));
}

function summaryToAsset(summary: ObjectSummary): PickedAsset {
  const asset: PickedAsset = {
    key: summary.key,
    publicUrl: summary.publicUrl,
    size: summary.size,
  };
  if (summary.contentType !== undefined) {
    asset.contentType = summary.contentType;
  }
  return asset;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export function AssetPicker({ bucket, open, onSelect, onCancel }: AssetPickerProps): ReactElement {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounced(searchInput, SEARCH_DEBOUNCE_MS);

  /**
   * Cursor stack: `cursorStack[length-1]` is the cursor for the *current*
   * page (null = first page); previous entries are kept so "Anterior" can
   * pop back without re-fetching from scratch.
   */
  const cursorStackRef = useRef<Array<string | null>>([null]);
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);

  // Reset paginação e busca a cada abertura (mirrors legacy `_open`).
  useEffect(() => {
    if (open) {
      cursorStackRef.current = [null];
      setCurrentCursor(null);
      setSearchInput('');
    }
  }, [open]);

  const query = useQuery({
    queryKey: ['admin', 'storage', 'objects', bucket, currentCursor],
    queryFn: () => fetchObjects(bucket, currentCursor),
    enabled: open && bucket.length > 0,
    staleTime: 30_000,
  });

  const filtered = useMemo(
    () => filterByQuery(query.data?.items ?? [], debouncedSearch),
    [query.data?.items, debouncedSearch],
  );

  const handleNext = useCallback((): void => {
    const next = query.data?.cursor;
    if (!next) return;
    cursorStackRef.current = [...cursorStackRef.current, next];
    setCurrentCursor(next);
  }, [query.data?.cursor]);

  const handlePrev = useCallback((): void => {
    const stack = cursorStackRef.current;
    if (stack.length <= 1) return;
    const popped = stack.slice(0, -1);
    cursorStackRef.current = popped;
    setCurrentCursor(popped[popped.length - 1] ?? null);
  }, []);

  const canGoBack = cursorStackRef.current.length > 1;
  const canGoNext = Boolean(query.data?.hasMore);

  const handleOpenChange = useCallback(
    (next: boolean): void => {
      if (!next) onCancel();
    },
    [onCancel],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl"
        onPointerDownOutside={(e) => {
          // Keep accidental clicks from dismissing while loading the first page.
          if (query.isLoading) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Escolher de {bucket}</DialogTitle>
          <DialogDescription>
            Selecione um arquivo já enviado ao bucket {bucket}.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          <Input
            type="search"
            placeholder="Buscar por nome do arquivo"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            prefix={<Search size={16} aria-hidden />}
            aria-label="Buscar assets por nome"
          />
        </div>

        <AssetGrid
          isLoading={query.isLoading}
          isError={query.isError}
          {...(query.error instanceof Error ? { errorMessage: query.error.message } : {})}
          onRetry={() => void query.refetch()}
          items={filtered}
          onPick={(asset) => onSelect(summaryToAsset(asset))}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handlePrev}
            disabled={!canGoBack || query.isFetching}
          >
            Anterior
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleNext}
            disabled={!canGoNext || query.isFetching}
          >
            Próxima
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Subcomponents                                                       */
/* ------------------------------------------------------------------ */

interface AssetGridProps {
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  onRetry: () => void;
  items: ObjectSummary[];
  onPick: (asset: ObjectSummary) => void;
}

function AssetGrid({
  isLoading,
  isError,
  errorMessage,
  onRetry,
  items,
  onPick,
}: AssetGridProps): ReactElement {
  if (isLoading) return <AssetGridSkeleton />;
  if (isError) {
    return (
      <div className="my-4">
        <ErrorState
          description={errorMessage ?? 'Falha ao carregar arquivos.'}
          onRetry={onRetry}
        />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="my-4">
        <EmptyState
          icon={FileIcon}
          title="Nenhum arquivo encontrado"
          description="Tente ajustar a busca ou enviar um novo arquivo."
        />
      </div>
    );
  }
  return (
    <div className="my-4 grid max-h-[420px] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {items.map((obj) => (
        <AssetTile key={obj.key} object={obj} onPick={onPick} />
      ))}
    </div>
  );
}

function AssetGridSkeleton(): ReactElement {
  const tiles = Array.from({ length: SKELETON_COUNT });
  return (
    <div className="my-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {tiles.map((_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton height={96} />
          <Skeleton height={12} width="80%" />
        </div>
      ))}
    </div>
  );
}

interface AssetTileProps {
  object: ObjectSummary;
  onPick: (asset: ObjectSummary) => void;
}

function AssetTile({ object, onPick }: AssetTileProps): ReactElement {
  const isImage = isImageContentType(object.contentType);
  return (
    <button
      type="button"
      onClick={() => onPick(object)}
      title={object.key}
      className={cn(
        'group flex flex-col items-center gap-2 rounded-card border border-border bg-bg-card p-2 text-left',
        'transition-all hover:border-primary hover:shadow-md focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-card',
      )}
    >
      <div className="flex h-24 w-full items-center justify-center overflow-hidden rounded-md bg-bg-hover">
        {isImage && object.publicUrl ? (
          <img
            src={object.publicUrl}
            alt={object.key}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <FileIcon size={28} aria-hidden className="text-muted" />
        )}
      </div>
      <span className="w-full break-all text-xs text-muted group-hover:text-white">
        {truncateName(object.key, MAX_NAME_LENGTH)}
      </span>
    </button>
  );
}
